import { memo, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useQRPendingOrders, useRejectQRPendingOrder } from "@/hooks/useQRPendingOrders";
import { ApiError } from "@/lib/api";
import {
  acceptErrorMessage,
  alreadyResolved,
  isNetworkError,
  myProfileId,
  ORDER_REMOVED_LABEL,
  qrTableLabel,
  resolvedLabel,
} from "@/lib/qrAccept";
import { acceptedToast, NO_ANSWER_TITLE } from "@/lib/qrAcceptPrint";
import { useAcceptAndPrint } from "./useAcceptAndPrint";
import { QRPendingOrderCard } from "./QRPendingOrderCard";
import { QRApproveModifyDialog } from "./QRApproveModifyDialog";
import { QRRejectDialog } from "./QRRejectDialog";
import { getPendingOrderId, type QRPendingOrder } from "@/types/qr-orders";
import { holdQRPendingPanelOpen } from "@/lib/qrPendingPanelOpen";

interface QRPendingOrdersPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Memoised: the Accept prompts re-render every countdown tick and render this too. */
export const QRPendingOrdersPanel = memo(function QRPendingOrdersPanel({ open, onOpenChange }: QRPendingOrdersPanelProps) {
  const { toast } = useToast();
  const { data, isLoading } = useQRPendingOrders("pending");
  // While open, the New QR Order prompts step aside (they'd sit on top of it).
  useEffect(() => (open ? holdQRPendingPanelOpen() : undefined), [open]);
  const { acceptAndPrint, isPending: accepting } = useAcceptAndPrint();
  const reject = useRejectQRPendingOrder();

  const [modifyTarget, setModifyTarget] = useState<QRPendingOrder | null>(null);
  const [rejectTarget, setRejectTarget] = useState<QRPendingOrder | null>(null);

  /** Accept: the items go to the kitchen (KOTs print here) and this person gets the table. */
  const handleAccept = async (order: QRPendingOrder) => {
    try {
      const { result, printing } = await acceptAndPrint(getPendingOrderId(order));
      onOpenChange(false);
      toast(acceptedToast(result.pending_order?.table_number ?? order.table_number, await printing));
    } catch (error) {
      const resolvedBy = alreadyResolved(error);
      const table = qrTableLabel(order.table_number);
      if (resolvedBy) {
        toast({ title: resolvedLabel(resolvedBy, myProfileId()), description: table });
      } else if (error instanceof ApiError && error.status === 404) {
        toast({ title: ORDER_REMOVED_LABEL, description: table });
      } else {
        toast({
          title: isNetworkError(error) ? NO_ANSWER_TITLE : "Accept failed",
          description: acceptErrorMessage(error),
          variant: "destructive",
        });
      }
    }
  };

  const handleResolved = () => {
    // Called by the modify/reject dialogs after a successful submit
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-md w-full p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-border">
            <SheetTitle>Pending QR Orders</SheetTitle>
            <SheetDescription>
              Guest orders waiting to be accepted
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !data?.items || data.items.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No pending orders
                </div>
              ) : (
                data.items.map((order) => (
                  <QRPendingOrderCard
                    key={getPendingOrderId(order)}
                    order={order}
                    onAccept={(o) => void handleAccept(o)}
                    onModify={(o) => setModifyTarget(o)}
                    onReject={(o) => setRejectTarget(o)}
                    isProcessing={accepting || reject.isPending}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <QRApproveModifyDialog
        order={modifyTarget}
        onClose={() => setModifyTarget(null)}
        onResolved={handleResolved}
      />
      <QRRejectDialog
        order={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onResolved={handleResolved}
      />
    </>
  );
});
